/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaStructureMenu.ts
 * FILE_ID: JOBHUB-QA-STRUCTURE-MENU-001
 * PURPOSE: Area and checklist copy/paste menus for the QA rails.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useContextMenu } from '@quotech/ui'
import type { QaApi, QaAreaExecution, QaPasteBody } from '../../qa/qaApi'
import { buildQaAreaMenu } from '../../qa/qaAreaMenu'
import { buildQaChecklistRailMenu } from '../../qa/qaChecklistRailMenu'
import { readQaClipboard, writeQaClipboard, type QaClipboardKind } from '../../qa/qaClipboard'
import { useQaPaste } from './useQaPaste'
import type { QaSiblingMove } from './useQaSiblingReorder'
import { applyQaDelete } from './qaSelectionWrites'
import { useQaLevelActions } from './useQaLevelActions'

type MenuTarget = {
  kind: 'area' | 'checklist' | 'level'
  id: string
  name: string
  areaId: string
}

export function useQaStructureMenu({
  api,
  jobId,
  inspectionId,
  setError,
  onCreated,
  onContainedIn,
  onRenameLevel,
  reorder,
}: {
  api: QaApi
  jobId: number
  inspectionId: string | null
  setError: (error: string | null) => void
  onCreated: (scope: 'areas' | 'checklists', id: string) => void
  onContainedIn?: (areaId: string) => void
  onRenameLevel?: (floorId: string) => void
  reorder?: {
    forArea: (areaId: string) => QaSiblingMove
    forChecklist: (areaId: string, checklistId: string) => QaSiblingMove
  }
}) {
  const queryClient = useQueryClient()
  const menu = useContextMenu()
  const ops = useQaPaste({ api, queryClient, jobId, setError })
  const [target, setTarget] = useState<MenuTarget | null>(null)
  const levels = useQaLevelActions({
    api,
    jobId,
    inspectionId,
    setError,
    onRename: (floorId) => onRenameLevel?.(floorId),
  })

  function copiedLabel(kind: QaClipboardKind): string | null {
    const clip = readQaClipboard()
    return clip?.kind === kind ? clip.label : null
  }

  function remember(kind: 'area' | 'checklist', id: string, name: string) {
    writeQaClipboard({
      kind,
      ids: [id],
      label: name,
      jobId,
      inspectionId: inspectionId ?? '',
    })
  }

  async function pasteAt(body: QaPasteBody, scope: 'areas' | 'checklists' | null) {
    const created = await ops.paste.mutateAsync(body)
    const id = created.created_ids[0]
    if (id && scope) onCreated(scope, id)
  }

  function itemsFor(row: MenuTarget) {
    if (row.kind === 'level') {
      return buildQaAreaMenu({
        pasteAreaLabel: levels.copiedLevelLabel(),
        pasteChecklistLabel: levels.copiedAreaLabel(),
        copyLabel: 'Copy level',
        pasteAboveLabel: 'Paste level above',
        pasteBelowLabel: 'Paste level below',
        pasteIntoLabel: 'Paste location into this level',
        emptyAreaHint: 'Copy a level first',
        emptyIntoHint: 'Copy a location first',
        insertLabel: 'Insert blank level',
        onCopy: () => levels.copy(row.id, row.name),
        onPasteAreaAbove: () => void levels.pasteLevel(row.id, 'above'),
        onPasteAreaBelow: () => void levels.pasteLevel(row.id, 'below'),
        onPasteChecklist: () => void levels.pasteLocation(row.id, (body) => ops.paste.mutateAsync(body)),
        onRename: () => onRenameLevel?.(row.id),
        onDuplicate: () => void levels.duplicate(row.id),
        onInsertBlank: () => void levels.insertBlank(),
        onDelete: () => void levels.remove(row.id, row.name),
        onMoveUp: () => levels.move(row.id, -1),
        onMoveDown: () => levels.move(row.id, 1),
        canMoveUp: levels.canMove(row.id, -1),
        canMoveDown: levels.canMove(row.id, 1),
      })
    }
    if (row.kind === 'area') {
      return buildQaAreaMenu({
        pasteAreaLabel: copiedLabel('area'),
        pasteChecklistLabel: copiedLabel('checklist'),
        onCopy: () => remember('area', row.id, row.name),
        onPasteAreaAbove: () => void pasteCopied('area', row, 'above'),
        onPasteAreaBelow: () => void pasteCopied('area', row, 'below'),
        onPasteChecklist: () => void pasteCopied('checklist', row, 'below'),
        onRename: () => onCreated('areas', row.id),
        onDuplicate: () => void duplicate('area', row.id),
        onInsertBlank: () => void insertBlank(row.id),
        onDelete: () => void remove('area', row.id, row.name),
        onContainedIn: onContainedIn ? () => onContainedIn(row.id) : undefined,
        ...(reorder ? {
          onMoveUp: () => reorder.forArea(row.id).onMoveUp(),
          onMoveDown: () => reorder.forArea(row.id).onMoveDown(),
          canMoveUp: reorder.forArea(row.id).canMoveUp,
          canMoveDown: reorder.forArea(row.id).canMoveDown,
        } : {}),
      })
    }
    const checklistMove = reorder?.forChecklist(row.areaId, row.id)
    return buildQaChecklistRailMenu({
      pasteChecklistLabel: copiedLabel('checklist'),
      pasteRowsLabel: copiedLabel('rows'),
      onCopy: () => remember('checklist', row.id, row.name),
      onPasteAbove: () => void pasteCopied('checklist', row, 'above'),
      onPasteBelow: () => void pasteCopied('checklist', row, 'below'),
      onPasteRows: () => void pasteCopied('rows', row, 'below'),
      onRename: () => onCreated('checklists', row.id),
      onDuplicate: () => void duplicate('checklist', row.id),
      onInsert: () => void insertChecklist(row.areaId, row.id),
      onDelete: () => void remove('checklist', row.id, row.name),
      ...(checklistMove ? {
        onMoveUp: () => checklistMove.onMoveUp(),
        onMoveDown: () => checklistMove.onMoveDown(),
        canMoveUp: checklistMove.canMoveUp,
        canMoveDown: checklistMove.canMoveDown,
      } : {}),
    })
  }

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['qa-areas'] })
    await queryClient.invalidateQueries({ queryKey: ['qa-floors'] })
    await queryClient.invalidateQueries({ queryKey: ['qa-area-exec'] })
  }

  async function duplicate(kind: 'area' | 'checklist', id: string) {
    try {
      // Reused from: packages/job-hub/src/jobs/qa/qaInspectionApi.ts (deleteArea) for the refresh path.
      const created = kind === 'area'
        ? await api.duplicateArea(id)
        : await api.duplicateChecklist(id)
      await refresh()
      onCreated(kind === 'area' ? 'areas' : 'checklists', created.id)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Could not duplicate that.')
    }
  }

  async function remove(kind: 'area' | 'checklist', id: string, name: string) {
    const message = kind === 'area'
      ? `Delete "${name}" and everything in it?`
      : `Delete "${name}" and its checks?`
    if (!window.confirm(message)) return
    try {
      const executions = queryClient
        .getQueriesData<QaAreaExecution>({ queryKey: ['qa-area-exec'] })
        .map(([, value]) => value)
        .filter((value): value is QaAreaExecution => Boolean(value?.id))
      await applyQaDelete({
        api,
        queryClient,
        inspectionId,
        executions,
        targets: kind === 'area'
          ? { items: [], checklists: [], areas: [id] }
          : { items: [], checklists: [id], areas: [] },
      })
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Could not delete that.')
    }
  }

  async function insertBlank(anchorId: string) {
    if (!inspectionId) return
    try {
      const created = await api.addTable(inspectionId, 'New table', false)
      await refresh()
      onCreated('areas', created.id)
      void anchorId
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Could not insert a blank area.')
    }
  }

  async function insertChecklist(areaId: string, anchorId: string) {
    try {
      const created = await api.addChecklist(areaId, 'New checklist')
      await refresh()
      onCreated('checklists', created.id)
      void anchorId
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Could not insert a checklist.')
    }
  }

  async function pasteCopied(
    kind: 'area' | 'checklist' | 'rows',
    row: MenuTarget,
    position: 'above' | 'below',
  ) {
    const clip = readQaClipboard()
    if (!clip || clip.kind !== kind) return
    if (kind === 'area') {
      if (!inspectionId) return
      await pasteAt({
        kind: 'area',
        source_ids: clip.ids,
        target_id: inspectionId,
        anchor_id: row.id,
        position,
      }, 'areas')
      return
    }
    if (kind === 'checklist') {
      await pasteAt({
        kind: 'checklist',
        source_ids: clip.ids,
        target_id: row.kind === 'area' ? row.id : row.areaId,
        anchor_id: row.kind === 'checklist' ? row.id : null,
        position,
      }, 'checklists')
      return
    }
    await pasteAt({
      kind: 'rows',
      source_ids: clip.ids,
      target_id: row.id,
      anchor_id: null,
      position: 'below',
    }, null)
  }

  return {
    menu,
    items: target ? itemsFor(target) : [],
    openLevel(floor: { id: string; floor_name: string }, event: { clientX: number; clientY: number }) {
      setTarget({ kind: 'level', id: floor.id, name: floor.floor_name, areaId: floor.id })
      menu.openAt(event)
    },
    openArea(area: { id: string; area_name: string }, event: { clientX: number; clientY: number }) {
      const next = { kind: 'area' as const, id: area.id, name: area.area_name, areaId: area.id }
      setTarget(next)
      menu.openAt(event)
    },
    openChecklist(
      checklistId: string,
      name: string,
      areaId: string,
      event: { clientX: number; clientY: number },
    ) {
      setTarget({ kind: 'checklist', id: checklistId, name, areaId })
      menu.openAt(event)
    },
    itemsForArea(area: { id: string; area_name: string }) {
      return itemsFor({ kind: 'area', id: area.id, name: area.area_name, areaId: area.id })
    },
    itemsForChecklist(checklistId: string, name: string, areaId: string) {
      return itemsFor({ kind: 'checklist', id: checklistId, name, areaId })
    },
  }
}
